import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight } from 'lucide-react';
import useSeo from '../../hooks/useSeo';

const INCLUDED = [
  'Custom design — not a template',
  'Mobile-first, fast, accessible',
  'Contact form wired and working',
  'Google Maps & business hours integration',
  'Hosted & deployed to your domain',
  'SSL certificate included',
  'One year of small updates',
  'Live demo built before you pay a dollar',
];

const NOT_INCLUDED = [
  'E-commerce / online store (different scope)',
  'Custom web app or SaaS features',
  'Ongoing SEO or ad management',
  'Post-launch edits beyond minor copy changes',
];

function Pricing() {
  useSeo({
    title: 'Pricing — $800 Flat for a Modern Website | Sudco Solutions',
    description:
      '$800 flat for a custom, mobile-first small-business website — demo built before you pay a dollar. Post-launch work billed at $150/hr in 15-minute increments.',
    path: '/pricing',
  });

  return (
    <div className="min-h-screen bg-bg text-text font-sans">
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-accent/8 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-sm text-accent-2 font-medium mb-4 uppercase tracking-wider">Flat fee. No surprises.</p>
            <div className="font-display font-bold leading-none mb-4">
              <span className="text-text-muted text-4xl md:text-5xl align-top mt-6 inline-block">$</span>
              <span className="text-8xl md:text-[10rem] gradient-text inline-block">800</span>
            </div>
            <p className="text-xl md:text-2xl text-text mb-3 font-display font-semibold">
              One price. Your whole new website.
            </p>
            <p className="text-text-muted text-lg max-w-xl mx-auto">
              No hourly surprises, no scope creep upcharges. You see a live demo before paying anything.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 gap-6"
          >
            <div className="glass rounded-2xl p-8">
              <h2 className="font-display font-bold text-2xl text-text mb-6">
                What's <span className="gradient-text">included</span>
              </h2>
              <ul className="space-y-3">
                {INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-text">
                    <Check size={16} className="text-accent-2 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-border bg-transparent rounded-2xl p-8">
              <h2 className="font-display font-bold text-2xl text-text mb-6">
                What's <span className="text-text-muted">not included</span>
              </h2>
              <ul className="space-y-3 mb-8">
                {NOT_INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-text-muted">
                    <X size={16} className="text-border mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="border-t border-border pt-6">
                <p className="text-sm font-semibold text-text mb-2">Post-launch work</p>
                <p className="text-sm text-text-muted leading-relaxed">
                  Any changes after launch — new pages, redesigns, copy updates — are billed at{' '}
                  <span className="text-text font-medium">$150/hr</span> in 15-minute increments, invoiced monthly via Stripe. No retainer required.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="mt-8 glass rounded-2xl p-8 text-center"
          >
            <p className="text-text-muted text-sm mb-1">How it works</p>
            <p className="font-display font-bold text-xl text-text mb-4">
              You see the demo first. Then you decide.
            </p>
            <p className="text-text-muted text-sm max-w-xl mx-auto mb-7 leading-relaxed">
              I build a live, working demo of your new site before any money changes hands. If you love it, $800 gets it shipped to your domain. If not, no hard feelings — keep the demo link.
            </p>
            <a
              href="/#contact"
              className="group bg-gradient-brand text-white px-7 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 hover:shadow-glow transition-shadow"
            >
              Get your free demo
              <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Pricing;
